import React, { useState, useEffect, useMemo } from 'react';
import { callsAPI } from '../services/api';
import { RefreshCw, Filter, Phone, PhoneIncoming, PhoneMissed, PhoneOutgoing, CheckCircle, XCircle, Clock } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const SummaryCard = ({ label, value, total, icon: Icon, color }) => {
  const percentage = typeof value === 'number' && total > 0 ? Math.round((value / total) * 100) : 0;
  
  return (
    <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200 flex items-center justify-between transition-transform hover:scale-[1.02]">
      <div>
        <p className="text-sm font-medium text-gray-500">{label}</p>
        <div className="flex items-baseline mt-1">
          <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
          {total > 0 && typeof value === 'number' && label !== 'Total Calls' && !label.includes('Duration') && (
            <span className="ml-2 text-xs font-medium text-gray-400">
              ({percentage}%)
            </span>
          )}
        </div>
      </div>
      <div className={`p-3 rounded-lg ${color} text-white`}>
        <Icon size={20} />
      </div>
    </div>
  );
};

const CallAnalytics = () => {
  const [rawData, setRawData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter States
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    selectedEmployee: '',
    selectedCallType: ''
  });

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await callsAPI.getAll();
      setRawData(response.data || []);
    } catch (error) {
      console.error('Error loading calls:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const employees = useMemo(() => Array.from(new Set(rawData.map(c => c.agent_name))).filter(Boolean), [rawData]);
  const callTypes = ['Incoming', 'Outgoing', 'Missed', 'Rejected'];

  useEffect(() => {
    let result = rawData;

    if (filters.dateFrom) {
      result = result.filter(c => new Date(c.date) >= new Date(filters.dateFrom));
    }

    if (filters.dateTo) {
      const nextDay = new Date(filters.dateTo);
      nextDay.setDate(nextDay.getDate() + 1);
      result = result.filter(c => new Date(c.date) < nextDay);
    }

    if (filters.selectedEmployee) {
      result = result.filter(c => c.agent_name === filters.selectedEmployee);
    }

    if (filters.selectedCallType) {
      result = result.filter(c => c.call_type === filters.selectedCallType);
    }

    setFilteredData(result);
  }, [rawData, filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Summary calculations
  const totalCalls = filteredData.length;
  const connected = filteredData.filter(c => c.status === 'Connected' || c.status === 'Answered').length;
  const notConnected = totalCalls - connected;
  const incoming = filteredData.filter(c => c.call_type === 'Incoming').length;
  const outgoing = filteredData.filter(c => c.call_type === 'Outgoing').length;
  const missed = filteredData.filter(c => c.call_type === 'Missed').length;

  const totalDurationSeconds = filteredData.reduce((acc, curr) => acc + (typeof curr.duration === 'number' ? curr.duration : 0), 0);
  const hours = Math.floor(totalDurationSeconds / 3600);
  const minutes = Math.floor((totalDurationSeconds % 3600) / 60);
  const durationString = `${hours}h ${minutes}m`;

  // Chart Data
  const callTypeData = callTypes.map(t => ({
    name: t,
    value: filteredData.filter(c => c.call_type === t).length
  }));

  const callsByDateData = useMemo(() => {
    const grouped = filteredData.reduce((acc, curr) => {
      const date = curr.date?.split('T')[0] || 'Unknown';
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {});
    return Object.keys(grouped).sort().map(d => ({ name: d, calls: grouped[d] }));
  }, [filteredData]);

  const callsByUserData = useMemo(() => {
    const users = Array.from(new Set(filteredData.map(c => c.agent_name))).filter(Boolean);
    return users.map(u => ({
      name: u,
      calls: filteredData.filter(c => c.agent_name === u).length
    }));
  }, [filteredData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500" data-testid="call-analytics-page">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">Call Analytics Dashboard</h2>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 text-gray-700 font-medium">
          <Filter size={18} />
          <span>Filters:</span>
        </div>
        
        <input 
          type="date" 
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
          value={filters.dateFrom}
          onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
        />
        
        <span className="text-gray-400">to</span>
        
        <input 
          type="date" 
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
          value={filters.dateTo}
          onChange={(e) => handleFilterChange('dateTo', e.target.value)}
        />

        <select 
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none bg-white min-w-[150px]"
          value={filters.selectedEmployee}
          onChange={(e) => handleFilterChange('selectedEmployee', e.target.value)}
        >
          <option value="">All Employees</option>
          {employees.map(emp => <option key={emp} value={emp}>{emp}</option>)}
        </select>

        <select 
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none bg-white min-w-[150px]"
          value={filters.selectedCallType}
          onChange={(e) => handleFilterChange('selectedCallType', e.target.value)}
        >
          <option value="">All Call Types</option>
          {callTypes.map(type => <option key={type} value={type}>{type}</option>)}
        </select>

        <button 
          onClick={loadData}
          className="ml-auto p-2 bg-teal-50 text-teal-600 rounded-lg hover:bg-teal-100 transition-colors"
          title="Refresh Data"
        >
          <RefreshCw size={20} />
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
        <SummaryCard label="Total Calls" value={totalCalls} total={0} icon={Phone} color="bg-blue-500" />
        <SummaryCard label="Incoming" value={incoming} total={totalCalls} icon={PhoneIncoming} color="bg-indigo-500" />
        <SummaryCard label="Outgoing" value={outgoing} total={totalCalls} icon={PhoneOutgoing} color="bg-amber-500" />
        <SummaryCard label="Missed" value={missed} total={totalCalls} icon={PhoneMissed} color="bg-rose-500" />
        <SummaryCard label="Connected" value={connected} total={totalCalls} icon={CheckCircle} color="bg-green-500" />
        <SummaryCard label="Not Connected" value={notConnected} total={totalCalls} icon={XCircle} color="bg-red-500" />
        <SummaryCard label="Total Duration" value={durationString} total={0} icon={Clock} color="bg-purple-500" />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Call Type Distribution</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={callTypeData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={80} />
                <Tooltip />
                <Bar dataKey="value" fill="#6366f1" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Calls by Date</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={callsByDateData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="calls" fill="#0d9488" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Calls by Employee</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={callsByUserData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={100} />
                <Tooltip />
                <Bar dataKey="calls" fill="#3b82f6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Call Duration by Date (Minutes)</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={callsByDateData.map(d => ({
                ...d,
                duration: Math.round(filteredData.filter(c => c.date?.startsWith(d.name)).reduce((acc, c) => acc + (c.duration || 0), 0) / 60)
              }))}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="duration" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallAnalytics;
